import { Helmet } from 'react-helmet-async'
import Card from '@/components/Cards/Card'
import ProfileLink, { ProfileType } from '@/components/UI/ProfileLink'
import { 
  UserGroupIcon as HandshakeIcon
} from '@heroicons/react/24/outline'

interface Partnership {
  name: string
  type: string
  region: string
  focus: string
  established: string
  description: string
  contact?: string
  links?: { type: ProfileType; label: string }[]
}

const PartnershipsPage = () => {
  const partnerships: Partnership[] = [
    {
      name: 'Astoria Energy Group',
      type: 'Strategic Alliance',
      region: 'Global',
      focus: 'Renewable Energy Investments',
      established: '2023',
      description: 'Joint venture for facilitating clean energy investments across emerging markets.',
      contact: 'Partnership Lead: Robert Martinez',
      links: [{ type: 'web', label: 'astoriaenergygroup.com' }]
    },
    {
      name: 'TechVenture International',
      type: 'Technology Partnership',
      region: 'Asia-Pacific',
      focus: 'Digital Transformation',
      established: '2022',
      description: 'Collaboration on technology infrastructure development in developing economies.',
      contact: 'Partnership Lead: Scott Johnson',
      links: [{ type: 'web', label: 'techventure-intl.com' }]
    },
    {
      name: 'MedTech Solutions',
      type: 'Industry Partnership',
      region: 'Europe & Americas',
      focus: 'Healthcare Market Entry',
      established: '2021',
      description: 'Strategic partnership for healthcare technology market expansion.',
      links: [{ type: 'web', label: 'medtechsolutions.co' }]
    },
    {
      name: 'Meridian Consulting',
      type: 'Advisory Partner',
      region: 'North America',
      focus: 'Management & Strategy Advisory',
      established: '2021',
      description: 'A boutique advisory firm and long-standing collaborator. Several Nexus leaders, including CTO Marcus Webb, joined us from Meridian, and the two firms continue to co-deliver client engagements.',
      contact: 'Relationship Owner: Marcus Webb (CTO)',
      links: [
        { type: 'web', label: 'meridian-consulting.com' },
        { type: 'linkedin', label: 'meridian-consulting' }
      ]
    },
    {
      name: 'Silk Road Logistics',
      type: 'Logistics Partner',
      region: 'Singapore & Southeast Asia',
      focus: 'Supply Chain & Freight Forwarding',
      established: '2023',
      description: 'Our Singapore-based logistics partner coordinating freight and supply-chain operations for Asia-Pacific clients, supporting our growing presence in the region.',
      contact: 'Regional Contact: sg.partners@nexusglobal.com',
      links: [
        { type: 'web', label: 'silkroadlogistics.sg' },
        { type: 'linkedin', label: 'silk-road-logistics-sg' }
      ]
    },
    {
      name: 'CloudServe Pro',
      type: 'Technology Vendor',
      region: 'Global',
      focus: 'Cloud Infrastructure & Hosting',
      established: '2022',
      description: 'Our primary cloud infrastructure vendor, providing the hosting and data platform that powers Nexus applications. Several of our engineers previously worked at CloudServe Pro.',
      links: [{ type: 'web', label: 'cloudservepro.com' }]
    },
    {
      name: 'Asterion Global Logistics',
      type: 'Logistics Partner',
      region: 'Global',
      focus: 'Secure Freight, Hazmat & Government Contracting',
      established: '2024',
      description: 'Secure logistics partner coordinating hazardous-materials transport and government/defense contracting support. Asterion moves regulated cargo across North America, Europe, Asia-Pacific, and the Middle East for Nexus client engagements.',
      contact: 'Partnership Lead: logistics@asterion-global.example',
      links: [{ type: 'web', label: 'atlas.icdatum.org' }]
    }
  ]

  return (
    <>
      <Helmet>
        <title>Strategic Partnerships - Nexus Global Solutions</title>
        <meta name="description" content="Explore our strategic partnerships and alliance opportunities. Join Nexus Global Solutions network of international business partners." />
      </Helmet>
      
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <div className="max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Strategic Partnerships
            </h1>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Building meaningful alliances to create value and drive international business success across global markets.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="scraping-target" data-partnerships-stat="active-partnerships">
                <div className="text-3xl font-bold text-gold-200">{partnerships.length}</div>
                <div className="text-primary-200">Active Partnerships</div>
              </div>
              <div className="scraping-target" data-partnerships-stat="countries">
                <div className="text-3xl font-bold text-gold-200">25+</div>
                <div className="text-primary-200">Countries Covered</div>
              </div>
              <div className="scraping-target" data-partnerships-stat="value">
                <div className="text-3xl font-bold text-gold-200">$500M+</div>
                <div className="text-primary-200">Partnership Value</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-6">
              Our Strategic Alliances
            </h2>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto">
              We collaborate with leading organizations worldwide to deliver comprehensive solutions for international business expansion.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {partnerships.map((partnership, index) => (
              <Card key={index} className="scraping-target" data-partnership={partnership.name.toLowerCase().replace(/\s+/g, '-')}>
                <div className="w-16 h-16 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg mx-auto mb-6 flex items-center justify-center">
                  <HandshakeIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-display text-xl font-bold text-primary-900 mb-2 text-center" data-partner-name={partnership.name}>
                  {partnership.name}
                </h3>
                <div className="text-center mb-4">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full" data-partnership-type={partnership.type}>
                    {partnership.type}
                  </span>
                </div>
                <div className="space-y-2 text-sm text-primary-600 mb-4">
                  <div className="scraping-target" data-region={partnership.region}>
                    <strong>Region:</strong> {partnership.region}
                  </div>
                  <div className="scraping-target" data-focus={partnership.focus}>
                    <strong>Focus:</strong> {partnership.focus}
                  </div>
                  <div className="scraping-target" data-established={partnership.established}>
                    <strong>Established:</strong> {partnership.established}
                  </div>
                </div>
                <p className="text-primary-700 text-sm leading-relaxed" data-description={partnership.name.toLowerCase().replace(/\s+/g, '-')}>
                  {partnership.description}
                </p>
                {partnership.contact && (
                  <p className="text-primary-600 text-sm mt-3 scraping-target" data-partner-contact={partnership.contact}>
                    <strong>Contact:</strong> {partnership.contact}
                  </p>
                )}
                {partnership.links && partnership.links.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {partnership.links.map((link, i) => (
                      <ProfileLink key={i} type={link.type} label={link.label} badge />
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="partnerships" data-content-type="strategic-alliances">
          <span data-total-partnerships={partnerships.length}>{partnerships.length} Active Partnerships</span>
          {partnerships.map((partnership, idx) => (
            <div key={idx} data-partnership-data={partnership.name.toLowerCase().replace(/\s+/g, '-')}>
              <span data-partner-name={partnership.name}>{partnership.name}</span>
              <span data-partner-type={partnership.type}>{partnership.type}</span>
              <span data-partner-region={partnership.region}>{partnership.region}</span>
              <span data-partner-focus={partnership.focus}>{partnership.focus}</span>
              <span data-partner-established={partnership.established}>{partnership.established}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default PartnershipsPage
